import { Plus, X } from "lucide-react";

// Edits an array of allocations.
//
// Allocation shape:
//   {
//     scope: "offer" | "department" | "agency",  // defaults to "offer" for legacy rows
//     offerId,                                    // when scope = "offer"
//     scopeId,                                    // department name when scope = "department"
//     mode: "percent" | "amount",
//     value,
//     costType: "cogs" | "opex"
//   }
//
// Distribution rules (handled in calculations.costFromAllocations):
//   - scope="offer"      → contributes only to that offer.
//   - scope="department" → applies across every offer in that department
//                          (auto-split for per-offer P&L views; full amount
//                          for that department's rollup).
//   - scope="agency"     → agency overhead. Subtracted only at agency level,
//                          NOT charged to any specific department/offer.
export default function OfferAllocationEditor({ value = [], onChange, offers = [], departments = [] }) {
  const allocations = Array.isArray(value) ? value : [];

  // Normalize a legacy row (no `scope` set) to scope="offer".
  const normalize = (a) => ({ ...a, scope: a.scope || (a.offerId ? "offer" : "offer") });

  const addRow = () => {
    onChange([...allocations, { scope: "offer", offerId: "", scopeId: "", mode: "percent", value: 0, costType: "cogs" }]);
  };

  const updateRow = (idx, patch) => {
    onChange(allocations.map((a, i) => (i === idx ? { ...normalize(a), ...patch } : a)));
  };

  const removeRow = (idx) => {
    onChange(allocations.filter((_, i) => i !== idx));
  };

  const totalPercent = allocations
    .filter((a) => a.mode === "percent")
    .reduce((sum, a) => sum + (Number(a.value) || 0), 0);
  const totalAmount = allocations
    .filter((a) => a.mode === "amount")
    .reduce((sum, a) => sum + (Number(a.value) || 0), 0);

  const recurringOffers = offers.filter((o) => o.type === "recurring");
  const oneOffOffers = offers.filter((o) => o.type === "oneoff");

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-brand-slate">Allocations</span>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue-dark font-medium"
        >
          <Plus size={11} /> Add allocation
        </button>
      </div>

      {allocations.length === 0 ? (
        <p className="text-xs text-brand-slate italic">No allocations yet — cost will count as Unassigned (won't appear in any P&L).</p>
      ) : (
        <div className="space-y-1.5">
          {allocations.map((a, idx) => {
            const scope = a.scope || (a.offerId ? "offer" : "offer");
            const costType = a.costType || "cogs";
            const costTypeStyle =
              costType === "cogs"
                ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                : costType === "marketing"
                ? "border-pink-300 bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300"
                : "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300";
            return (
              <div key={idx} className="flex items-center gap-1.5 flex-wrap">
                {/* Scope picker (Offer / Department / Agency) */}
                <select
                  value={scope}
                  onChange={(e) => {
                    const next = e.target.value;
                    // Reset the scope-specific target field on change.
                    const patch = { scope: next, offerId: "", scopeId: "" };
                    if (next === "offer") patch.offerId = "";
                    if (next === "department") patch.scopeId = departments[0] || "";
                    updateRow(idx, patch);
                  }}
                  className="px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                  title="Allocate by offer, department, or agency"
                >
                  <option value="offer">Offer</option>
                  <option value="department">Dept</option>
                  <option value="agency">Agency</option>
                </select>

                {/* Scope target — offer dropdown / department dropdown / "—" */}
                {scope === "offer" && (
                  <select
                    value={a.offerId || ""}
                    onChange={(e) => updateRow(idx, { offerId: e.target.value })}
                    className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                  >
                    <option value="">— Select offer —</option>
                    {recurringOffers.length > 0 && (
                      <optgroup label="Recurring">
                        {recurringOffers.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {oneOffOffers.length > 0 && (
                      <optgroup label="One-off">
                        {oneOffOffers.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                )}
                {scope === "department" && (
                  <select
                    value={a.scopeId || ""}
                    onChange={(e) => updateRow(idx, { scopeId: e.target.value })}
                    className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                  >
                    <option value="">— Select department —</option>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
                {scope === "agency" && (
                  <span className="flex-1 min-w-[140px] px-2 py-1.5 text-xs text-brand-slate italic">All offers (agency overhead)</span>
                )}

                <select
                  value={a.mode || "percent"}
                  onChange={(e) => updateRow(idx, { mode: e.target.value })}
                  className="px-2 py-1.5 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                >
                  <option value="percent">%</option>
                  <option value="amount">$</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step={a.mode === "percent" ? "1" : "0.01"}
                  value={a.value ?? 0}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  className="w-20 px-2 py-1.5 text-right border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
                />
                <select
                  value={costType}
                  onChange={(e) => updateRow(idx, { costType: e.target.value })}
                  className={`px-2 py-1.5 border rounded text-[10px] font-semibold uppercase tracking-wide focus:outline-none focus:ring-1 focus:ring-brand-blue/30 ${costTypeStyle}`}
                  title={
                    costType === "cogs" ? "Direct delivery cost (counts in COGS)"
                    : costType === "marketing" ? "Marketing / acquisition cost (rolls into OPEX in P&L, tagged for visibility)"
                    : "Overhead (counts in OPEX)"
                  }
                >
                  <option value="cogs">COGS</option>
                  <option value="marketing">MARKETING</option>
                  <option value="opex">OPEX</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="p-1.5 rounded hover:bg-red-50 text-red-600"
                  title="Remove allocation"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
          <div className="pt-1 text-[11px] text-brand-slate">
            Total: {totalPercent > 0 && <span className={totalPercent > 100 ? "text-red-600 font-medium" : ""}>{totalPercent}%</span>}
            {totalPercent > 0 && totalAmount > 0 && <span> + </span>}
            {totalAmount > 0 && <span>${totalAmount.toLocaleString()} fixed</span>}
            {totalPercent < 100 && allocations.some((a) => a.mode === "percent") && (
              <span className="ml-2 text-brand-slate"> · {100 - totalPercent}% remainder = Unassigned</span>
            )}
            {totalPercent > 100 && (
              <span className="ml-2 text-red-600 font-medium"> · over 100% — reduce some allocations</span>
            )}
          </div>
          <p className="text-[10px] text-brand-slate leading-relaxed">
            <strong>Offer</strong> = applies to one offer. <strong>Dept</strong> = applies to every offer in that department (auto-split). <strong>Agency</strong> = overhead, reduces agency net only (doesn't drag department margins).
            <br />
            <span className="font-semibold text-emerald-700">COGS</span> = direct delivery cost (counts in Gross Profit).
            {" "}
            <span className="font-semibold text-amber-700">OPEX</span> = overhead (deducted after Gross Profit to get Net Profit).
          </p>
        </div>
      )}
    </div>
  );
}
